// "Customer messages not about a quote": what sellers sent from Your account under
// "Selling something new" or "Something else". Staff answer by email, then mark the
// message handled. Shows nothing while there are none, so the page is as it was.
//
// Worker routes: GET /api/deals/customer-enquiries, POST /api/deals/customer-enquiries/:id/handled.
import { useCallback, useEffect, useState } from 'react';
import { api, formatDateTime } from '../../../utils/api';

const LIST_URL = '/api/deals/customer-enquiries';
const handledUrl = (id) => `${LIST_URL}/${encodeURIComponent(id)}/handled`;
const TOPIC_LABELS = { selling_new: 'Selling something new', other: 'Something else' };

export default function CustomerEnquiriesPanel() {
    const [enquiries, setEnquiries] = useState([]);
    const [busyId, setBusyId] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        try {
            const data = await api.get(LIST_URL);
            setEnquiries(data.enquiries || []);
        } catch {
            // A failed read leaves the page as it was: this panel is supplementary.
            setEnquiries([]);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function markHandled(id) {
        setBusyId(id);
        setError(null);
        try {
            await api.post(handledUrl(id), {});
            setEnquiries((current) => current.filter((each) => each.customer_enquiry_id !== id));
        } catch (e) {
            setError(e.message || 'Could not mark this message as handled');
        } finally {
            setBusyId(null);
        }
    }

    if (!enquiries.length) return null;
    return (
        <details className="card" data-testid="customer-enquiries" style={{ padding: 'var(--space-3)' }}>
            <summary>
                <strong>Customer messages not about a quote</strong>{' '}
                <span className="badge badge-neutral">{enquiries.length}</span>
            </summary>
            {error && <p role="alert" className="qa-muted">{error}</p>}
            <ul style={{ listStyle: 'none', padding: 0, margin: 'var(--space-2) 0 0' }}>
                {enquiries.map((each) => (
                    <li key={each.customer_enquiry_id} data-testid="customer-enquiry" style={{ marginBottom: 'var(--space-3)' }}>
                        <div>
                            <strong>{each.customer_name || 'A customer'}</strong>
                            {each.customer_email && <> ({each.customer_email})</>}
                            {' · '}{TOPIC_LABELS[each.topic] || each.topic}
                            {' · '}{formatDateTime(each.sent_at)}
                        </div>
                        <p style={{ whiteSpace: 'pre-wrap', margin: 'var(--space-1) 0' }}>{each.body}</p>
                        <button
                            className="btn btn-sm"
                            style={{ minHeight: 44 }}
                            disabled={busyId === each.customer_enquiry_id}
                            onClick={() => markHandled(each.customer_enquiry_id)}
                        >
                            Mark handled
                        </button>
                    </li>
                ))}
            </ul>
        </details>
    );
}
